import { Menu, Group, Avatar, Text, rem, Box, useMantineColorScheme } from '@mantine/core';
import {
  IconUser,
  IconLogout,
  IconChevronDown,
  IconMoon,
  IconSun,
  IconHelp,
  IconBell,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { useCustomRoles } from '@/hooks/api/useCustomRoles';
import { getInitials } from '@/lib/utils';
import { useMemo } from 'react';

export function Header() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { organization, membership, organizationId } = useOrganization();
  const { data: customRoles = [] } = useCustomRoles(organizationId || '');
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  // Get role display name
  const roleDisplay = useMemo(() => {
    if (!membership) return '';
    if (membership.is_admin) return 'Admin';
    if (membership.custom_role_id) {
      const role = customRoles.find(r => r.id === membership.custom_role_id);
      return role?.name || 'Member';
    }
    return membership.role?.charAt(0).toUpperCase() + membership.role?.slice(1) || 'Member';
  }, [membership, customRoles]);

  const userInitials = getInitials(user?.user_metadata?.full_name || user?.email);

  return (
    <Group>
      <Menu shadow="md" width={200} position="bottom-end">
        <Menu.Target>
          <Group style={{ cursor: 'pointer' }} gap="xs">
            <Avatar color="blue" radius="xl" size="sm">
              {userInitials}
            </Avatar>
            <Box visibleFrom="sm">
              <Text size="sm" fw={500}>
                {user?.user_metadata?.full_name || user?.email?.split('@')[0]}
              </Text>
              {organization && (
                <Text size="xs" c="dimmed">
                  {organization.name} • {roleDisplay}
                </Text>
              )}
            </Box>
            <IconChevronDown size={16} />
          </Group>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>Account</Menu.Label>
          <Menu.Item
            leftSection={<IconUser style={{ width: rem(14), height: rem(14) }} />}
            onClick={() => navigate('/profile')}
          >
            Profile
          </Menu.Item>
          <Menu.Item
            leftSection={<IconBell style={{ width: rem(14), height: rem(14) }} />}
            onClick={() => navigate('/notifications')}
          >
            Notifications
          </Menu.Item>
          {/* Help - only shown in dropdown on mobile (sidebar hidden) */}
          <Box hiddenFrom="sm">
            <Menu.Item
              leftSection={<IconHelp style={{ width: rem(14), height: rem(14) }} />}
              onClick={() => navigate('/help')}
            >
              Help & Support
            </Menu.Item>
          </Box>

          <Menu.Divider />

          <Menu.Item
            leftSection={
              colorScheme === 'dark' ? (
                <IconSun style={{ width: rem(14), height: rem(14) }} />
              ) : (
                <IconMoon style={{ width: rem(14), height: rem(14) }} />
              )
            }
            onClick={toggleColorScheme}
          >
            {colorScheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </Menu.Item>

          <Menu.Divider />

          <Menu.Item
            color="red"
            leftSection={<IconLogout style={{ width: rem(14), height: rem(14) }} />}
            onClick={logout}
          >
            Logout
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
